import { useState, useEffect } from 'react';

// Cache für Geocoding-Ergebnisse (localStorage)
const GEOCODE_CACHE_KEY = 'geocode_cache';
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 Tage

// Geocoding-Cache laden
function getGeocodeCache() {
  try {
    const cache = localStorage.getItem(GEOCODE_CACHE_KEY);
    return cache ? JSON.parse(cache) : {};
  } catch {
    return {};
  }
}

// Geocoding-Cache speichern
function saveGeocodeCache(cache) {
  try {
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage voll - alten Cache löschen
    localStorage.removeItem(GEOCODE_CACHE_KEY);
  }
}

// Adresse zu Koordinaten (Nominatim/OpenStreetMap - kostenlos)
async function geocodeAddress(address) {
  if (!address || address.trim().length < 5) return null;

  // Cache prüfen
  const cache = getGeocodeCache();
  const cacheKey = address.toLowerCase().trim();

  if (cache[cacheKey] && (Date.now() - cache[cacheKey].timestamp) < CACHE_DURATION) {
    return cache[cacheKey].coords;
  }

  try {
    // Nominatim API (OpenStreetMap) - kostenlos, 1 Request/Sekunde Limit
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=de,at,ch&limit=1`,
      {
        headers: {
          'User-Agent': 'WerkPilot-CRM/1.0'
        }
      }
    );

    if (!response.ok) return null;

    const data = await response.json();

    if (data && data.length > 0) {
      const coords = {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon)
      };

      // In Cache speichern
      cache[cacheKey] = {
        coords,
        timestamp: Date.now()
      };
      saveGeocodeCache(cache);

      return coords;
    }
  } catch (error) {
    console.error('Geocoding-Fehler:', error);
  }

  return null;
}

// Haversine-Formel: Entfernung zwischen zwei Koordinaten in km
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Erdradius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Entfernung formatieren
function formatDistance(km) {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  } else if (km < 10) {
    return `${km.toFixed(1)} km`;
  } else {
    return `${Math.round(km)} km`;
  }
}

// Geschätzte Fahrzeit berechnen (basierend auf Luftlinie)
function estimateDriveTime(luftlinieKm) {
  // Straßenentfernung ist ca. 1.4x Luftlinie
  const strasseKm = luftlinieKm * 1.4;

  // Durchschnittsgeschwindigkeit je nach Entfernung
  let avgSpeed;
  if (strasseKm < 5) {
    avgSpeed = 25; // Stadtverkehr
  } else if (strasseKm < 15) {
    avgSpeed = 35; // Stadtrand/gemischt
  } else if (strasseKm < 40) {
    avgSpeed = 50; // Überland
  } else {
    avgSpeed = 65; // Längere Strecke mit Schnellstraße
  }

  const minutes = Math.round((strasseKm / avgSpeed) * 60);
  return minutes;
}

// Fahrzeit formatieren
function formatDriveTime(minutes) {
  if (minutes < 1) {
    return '< 1 Min.';
  } else if (minutes < 60) {
    return `~${minutes} Min.`;
  } else {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `~${hours} Std.`;
    }
    return `~${hours} Std. ${mins} Min.`;
  }
}

/**
 * Hook zur Berechnung der Entfernung zwischen Firmenstandort und Kundenadresse
 * @param {string} customerAddress - Die Kundenadresse
 * @param {object} firmendaten - Firmendaten mit strasse, plz, ort
 * @returns {object} { distance, formattedDistance, driveTime, formattedDriveTime, loading, error }
 */
export function useDistance(customerAddress, firmendaten) {
  const [distance, setDistance] = useState(null);
  const [formattedDistance, setFormattedDistance] = useState(null);
  const [driveTime, setDriveTime] = useState(null);
  const [formattedDriveTime, setFormattedDriveTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Firmenadresse zusammensetzen
    const companyAddress = firmendaten
      ? `${firmendaten.strasse || ''}, ${firmendaten.plz || ''} ${firmendaten.ort || ''}`.trim()
      : null;

    // Prüfen ob beide Adressen vorhanden
    if (!customerAddress || !companyAddress || companyAddress.length < 5) {
      setDistance(null);
      setFormattedDistance(null);
      setDriveTime(null);
      setFormattedDriveTime(null);
      return;
    }

    let cancelled = false;

    async function calculateDistanceAsync() {
      setLoading(true);
      setError(null);

      try {
        // Beide Adressen geocoden (parallel)
        const [customerCoords, companyCoords] = await Promise.all([
          geocodeAddress(customerAddress),
          geocodeAddress(companyAddress)
        ]);

        if (cancelled) return;

        if (customerCoords && companyCoords) {
          const dist = calculateDistance(
            companyCoords.lat, companyCoords.lon,
            customerCoords.lat, customerCoords.lon
          );
          const time = estimateDriveTime(dist);
          setDistance(dist);
          setFormattedDistance(formatDistance(dist));
          setDriveTime(time);
          setFormattedDriveTime(formatDriveTime(time));
        } else {
          setDistance(null);
          setFormattedDistance(null);
          setDriveTime(null);
          setFormattedDriveTime(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setDistance(null);
          setFormattedDistance(null);
          setDriveTime(null);
          setFormattedDriveTime(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    // Kleine Verzögerung um Rate-Limits zu respektieren
    const timer = setTimeout(calculateDistanceAsync, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customerAddress, firmendaten?.strasse, firmendaten?.plz, firmendaten?.ort]);

  return { distance, formattedDistance, driveTime, formattedDriveTime, loading, error };
}
